import React from "react";

const COLORS = {
  WHITE: "#FFFFFF",
  BLUE_BG: "#D6EFFF",
  BLUE_BORDER: "#5B9FD4",
  DARK_OVERLAY: "rgba(0,0,0,0.5)",
  GREY_OVERLAY: "rgba(0,0,0,0.6)",
  PRIMARY_BLUE: "#713DC4",
  PRIMARY_ORANGE: "#F5A623",
  TEXT_WHITE: "#FFFFFF",
  SHADOW: "rgba(0,0,0,0.3)",
};

const styles: Record<string, React.CSSProperties> = {
  // ─── Root container ───────────────────────────────────────────────────────
  screenWrapper: {
    position: "fixed",
    inset: 0,
    overflow: "hidden",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },

  // ─── Home button ──────────────────────────────────────────────────────────
  homeBtn: {
    position: "absolute",
    top: 20,
    left: 24,
    zIndex: 10,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 11,
    borderRadius: 12,
    boxShadow: `0 2px 6px ${COLORS.SHADOW}`,
    backgroundColor: COLORS.WHITE,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  homeIconLottie: {
    width: 30,
    height: 30,
  },
  musicBtn: {
    position: "absolute",
    top: 16,
    right: 36,
    zIndex: 10,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 10,
    borderRadius: 16,
    boxShadow: `0 2px 6px rgba(0,0,0,0.3)`,
    backgroundColor: COLORS.WHITE,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 50,
    height: 50,
  },

  // ─── Scrollable journey path ──────────────────────────────────────────────
  scrollContainer: {
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    paddingBottom: 40,
  },
  journeyPathContainer: {
    position: "relative",
    width: "100%",
    margin: "0 auto",
  },

  // ─── Zigzag level row ─────────────────────────────────────────────────────
  levelRow: {
    position: "relative",
    width: "100%",
    height: 200,
    display: "flex",
    alignItems: "center",
  },
  zigzagBg: {
    position: "absolute",
    top: 0,
    height: "100%",
    width: "80%",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    zIndex: 0,
  },

  // ─── Card button (invisible touch target) ─────────────────────────────────
  cardButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
    position: "absolute",
    zIndex: 2,
  },

  // ─── Current level card (blue) ────────────────────────────────────────────
  currentCardWrapper: {
    position: "relative",
  },
  yetiDanceImg: {
    width: 80,
    height: 80,
    position: "absolute",
    bottom: 0,
    right: "100%",
    marginRight: 8,
  },
  cardImageWrapper: {
    width: 150,
    height: 150,
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  playIconRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  playIcon: {
    width: 32,
    height: 32,
    marginTop: -8,
  },
  cardInfoRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-evenly",
    width: "100%",
    marginTop: 4,
    padding: "0 8px",
  },
  cardStoryImageBox: {
    backgroundColor: COLORS.WHITE,
    borderRadius: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    flexShrink: 0,
  },
  storyLottie: {
    width: 32,
    height: 32,
  },
  questionMark: {
    fontSize: 18,
    fontWeight: 700,
    color: "#333",
  },
  cardText: {
    color: COLORS.WHITE,
    fontSize: 11,
    fontWeight: 600,
    width: 70,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    lineHeight: 1.2,
    textAlign: "left",
    marginLeft: 4,
  },

  // ─── Completed level card (orange bg) ────────────────────────────────────
  completedCardWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  gemsRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 2,
    backgroundColor: COLORS.BLUE_BG,
    border: `1px solid ${COLORS.BLUE_BORDER}`,
    borderRadius: 5,
    padding: "2px 8px",
    marginBottom: 4,
    marginTop: -4,
    alignSelf: "center",
  },
  gemIcon: {
    width: 18,
    height: 18,
  },

  // ─── Floating backpack button ─────────────────────────────────────────────
  backpackBtn: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: 180,
    height: 180,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    zIndex: 5,
  },
  backpackLottie: {
    width: "100%",
    height: "100%",
  },

  // ─── Loading overlay ──────────────────────────────────────────────────────
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: COLORS.DARK_OVERLAY,
    zIndex: 20,
  },
  loadingSpinner: {
    width: 48,
    height: 48,
    border: "5px solid rgba(255,255,255,0.3)",
    borderTopColor: COLORS.WHITE,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  // ─── Story selection modal ────────────────────────────────────────────────
  modalOverlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: COLORS.GREY_OVERLAY,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 30,
    // backgroundColor: "green",
  },
  modalContainer: {
    position: "relative",
    width: "95%",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    height: "100%",
  },
  modalCloseBtn: {
    position: "absolute",
    top: 20,
    right: 20,
    background: "none",
    border: "none",
    cursor: "pointer",
    zIndex: 1,
    padding: 4,
  },
  storyBookWrapper: {
    position: "relative",
    width: 546,
    height: 546,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  storyBookImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  storyBookTitle: {
    position: "absolute",
    color: COLORS.WHITE,
    fontWeight: 700,
    fontSize: 40,
    textAlign: "center",
    maxWidth: "45%",
    lineHeight: 1.3,
    // top: "43%",
    left: "49%",
    transform: "translateX(-55%)",
    // backgroundColor: "red",
  },
  modalButtonRow: {
    display: "flex",
    justifyContent: "center",
  },
  continueBtn: {
    backgroundColor: COLORS.PRIMARY_BLUE,
    color: COLORS.WHITE,
    border: "none",
    borderRadius: 30,
    padding: "16px 64px",
    fontSize: 30,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: `0 4px 14px ${COLORS.SHADOW}`,
    letterSpacing: 0.5,
  },

  // ─── Magic backpack modal ─────────────────────────────────────────────────
  backpackOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 30,
    overflow: "hidden",
    backgroundColor: COLORS.GREY_OVERLAY,
  },
  backpackCloseBtn: {
    position: "absolute",
    top: 28,
    right: 28,
    background: "none",
    border: "none",
    cursor: "pointer",
    zIndex: 40,
    padding: 4,
  },
  backpackBgWrapper: {
    position: "absolute",
    top: -500,
    left: 0,
    right: 0,
    height: "140%",
    backgroundSize: "cover",
    backgroundPosition: "center top",
    backgroundRepeat: "no-repeat",
  },
  backpackObjectsRow: {
    position: "absolute",
    top: "12%",
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "flex-start",
    justifyContent: "space-evenly",
    paddingLeft: "10%",
    paddingRight: "10%",
  },
  backpackObjectBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    width: 300,
    height: 300,
  },
  backpackObjectLottie: {
    width: "100%",
    height: "100%",
  },
  backpackBagWrapper: {
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: 300,
    height: 300,
  },
  backpackBagLottie: {
    width: "100%",
    height: "100%",
  },
};

export default styles;
